import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ErrorFallback } from "@/components/fallbacks";
import { Button, buttonVariants } from "@/components/ui/button";
import { AuthLangSwitcher } from "@/modules/auth/AuthLangSwitcher";

export const Route = createFileRoute("/auth")({
	beforeLoad: ({ location }) => {
		if (location.href === "/auth/") {
			throw redirect({ to: "/auth/login" });
		}
	},
	component: () => (
		<AuthLayout>
			<Outlet />
		</AuthLayout>
	),
	errorComponent: function Component({ error, reset }) {
		const { t } = useTranslation();

		return (
			<AuthLayout>
				<ErrorFallback title={t(($) => $.common.status.pageError.title)}>
					<p className="max-w-md text-center text-muted-foreground">
						{error.message || t(($) => $.common.status.pageError.message)}
					</p>

					<div className="flex flex-col items-center justify-center">
						<Button className="mt-4" onClick={() => reset()} variant={"secondary"}>
							{t(($) => $.common.status.pageError.action)}
						</Button>

						<Link
							className={buttonVariants({
								className: "mt-4",
							})}
							to={"/"}
						>
							{t(($) => $.common.status.pageNotFound.action)}
						</Link>
					</div>
				</ErrorFallback>
			</AuthLayout>
		);
	},
	notFoundComponent: function Component() {
		const { t } = useTranslation();

		return (
			<AuthLayout>
				<ErrorFallback title={t(($) => $.common.status.pageNotFound.title)}>
					<p className="max-w-md text-center text-muted-foreground">{t(($) => $.common.status.pageNotFound.message)}</p>

					<Link
						className={buttonVariants({
							className: "mt-4",
						})}
						to={"/"}
					>
						{t(($) => $.common.status.pageNotFound.action)}
					</Link>
				</ErrorFallback>
			</AuthLayout>
		);
	},
});

export function AuthLayout({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation();

	return (
		<div className="flex min-h-dvh items-center justify-center bg-background px-4 py-12 sm:px-6 lg:px-8">
			<div className="absolute top-4 left-4">
				<Link className="flex items-center gap-2 text-primary hover:text-primary/90" to="/">
					<ArrowLeftIcon className="h-5 w-5" />
					<span>{t(($) => $.modules.auth.common.buttons.back)}</span>
				</Link>
			</div>

			<div className="absolute top-4 right-4">
				<AuthLangSwitcher />
			</div>

			{children}
		</div>
	);
}
